/**
 * Gestion du consentement RGPD (stockage local, aucun cookie tiers).
 * Catégories : "necessary" (toujours actif) et "analytics" (mesure d'audience interne).
 * Le consentement expire au bout de 13 mois (recommandation CNIL).
 */

export type ConsentCategories = {
  necessary: true;
  analytics: boolean;
};

export type ConsentState = ConsentCategories & { date: string; version: number };

const STORAGE_KEY = "mg-cookie-consent";
export const CONSENT_VERSION = 1;
export const CONSENT_EVENT = "mg-consent-change";
/** Durée de validité du consentement : 13 mois. */
export const CONSENT_MAX_AGE_MS = 13 * 30 * 24 * 60 * 60 * 1000;

export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (parsed?.version !== CONSENT_VERSION) return null;
    const date = typeof parsed.date === "string" ? parsed.date : new Date().toISOString();
    const age = Date.now() - new Date(date).getTime();
    if (!Number.isFinite(age) || age > CONSENT_MAX_AGE_MS) {
      // Consentement périmé : on le supprime pour redemander l'accord.
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      date,
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

export function saveConsent(analytics: boolean): ConsentState {
  const state: ConsentState = {
    necessary: true,
    analytics,
    date: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* stockage indisponible : consentement valable pour la session courante */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
  return state;
}

export function resetConsent() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
}

/** Vrai uniquement si l'utilisateur a explicitement accepté la mesure d'audience. */
export function hasAnalyticsConsent(): boolean {
  return readConsent()?.analytics === true;
}
